"use client";

import React, { useEffect, useRef } from "react";
import Link from "next/link";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const SUBSTACK_URL = "https://pickmeupcookie.substack.com";

interface BlogPost {
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
}

const blogPosts: BlogPost[] = [
  {
    slug: "why-clean-ingredients-matter",
    category: "Nutrition",
    title: "Why Clean Ingredients Matter More Than You Think",
    excerpt:
      "Most snack bars hide behind long ingredient lists full of fillers and artificial flavours. We break down what to look for — and what to avoid — so you can fuel your body with intention.",
    date: "March 18, 2026",
    readTime: "4 min read",
  },
  {
    slug: "protein-and-mental-clarity",
    category: "Wellness",
    title: "The Surprising Link Between Protein and Mental Clarity",
    excerpt:
      "Protein isn't just for muscles. Emerging research shows that adequate protein intake plays a key role in mood regulation, focus, and sustained energy throughout the day.",
    date: "March 10, 2026",
    readTime: "5 min read",
  },
  {
    slug: "building-community-in-montreal",
    category: "Community",
    title: "How We Built a Community in the Old Port of Montréal",
    excerpt:
      "Before the cookie, there was the café. We share the story of how a small space in the old port became a gathering place for people who just needed a Pick Me Up.",
    date: "February 28, 2026",
    readTime: "6 min read",
  },
  {
    slug: "morning-rituals-for-a-positive-spirit",
    category: "Lifestyle",
    title: "5 Morning Rituals for a Positive Spirit",
    excerpt:
      "How you start your morning sets the tone for everything that follows. These five simple habits can shift your mindset and help you show up as your best self — every single day.",
    date: "February 14, 2026",
    readTime: "3 min read",
  },
  {
    slug: "organic-coffee-and-your-gut",
    category: "Nutrition",
    title: "Organic Coffee and Your Gut: What the Science Says",
    excerpt:
      "Not all coffee is created equal. We explore how organic, single-origin coffee differs from conventional blends and what that means for your gut health and daily energy.",
    date: "January 30, 2026",
    readTime: "5 min read",
  },
  {
    slug: "snacking-without-guilt",
    category: "Wellness",
    title: "Snacking Without Guilt: A New Way to Think About Food",
    excerpt:
      "Guilt has no place at the snack table. We believe food should nourish and delight — not stress you out. Here's how to build a healthier relationship with between-meal eating.",
    date: "January 15, 2026",
    readTime: "4 min read",
  },
];

const categoryColors: Record<string, string> = {
  Nutrition: "bg-pmu-amber-pale text-pmu-amber",
  Wellness: "bg-green-50 text-green-700",
  Community: "bg-blue-50 text-blue-700",
  Lifestyle: "bg-rose-50 text-rose-700",
};

export default function BlogPage() {
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("is-visible");
        });
      },
      { threshold: 0 }
    );
    pageRef?.current?.querySelectorAll(".reveal-hidden")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <Header />
      <main ref={pageRef} className="bg-pmu-cream min-h-screen pt-24">
        {/* Hero */}
        <section className="section-pad bg-pmu-cream">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <div className="reveal-hidden">
              <p
                className="text-xs font-medium uppercase tracking-widest text-pmu-taupe mb-6"
                style={{ letterSpacing: "0.2em" }}
              >
                Thoughts & Stories
              </p>
              <h1
                className="font-fraunces font-bold text-pmu-espresso mb-6"
                style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
              >
                The Pick Me Up Blog
              </h1>
              <p
                className="text-pmu-taupe leading-relaxed max-w-2xl mx-auto mb-10"
                style={{ fontSize: "1.0625rem" }}
              >
                Honest conversations about nutrition, wellness, community, and living your best life — no fluff, no filler.
              </p>
            </div>

            {/* Substack CTA Banner */}
            <div className="reveal-hidden delay-1">
              <div
                className="inline-flex flex-col sm:flex-row items-center gap-4 px-8 py-6 rounded-3xl"
                style={{ background: "#FFFFFF", border: "1px solid rgba(214, 207, 196, 0.5)" }}
              >
                <div className="text-left">
                  <p className="font-fraunces font-semibold text-pmu-espresso text-lg leading-snug">
                    Read more on our Substack
                  </p>
                  <p className="text-pmu-taupe text-sm mt-1">
                    Subscribe for weekly posts delivered straight to your inbox.
                  </p>
                </div>
                <a
                  href={SUBSTACK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary whitespace-nowrap shrink-0"
                >
                  Visit Substack →
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="section-pad bg-pmu-warm">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="reveal-hidden text-center mb-14">
              <p
                className="text-xs font-medium uppercase tracking-widest text-pmu-taupe mb-4"
                style={{ letterSpacing: "0.2em" }}
              >
                Latest Posts
              </p>
              <h2
                className="font-fraunces font-bold text-pmu-espresso"
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)" }}
              >
                What we've been writing about
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogPosts.map((post, i) => (
                <article
                  key={post.slug}
                  className={`reveal-hidden delay-${(i % 3) + 1} flex flex-col p-8 rounded-3xl bg-white transition-shadow hover:shadow-md`}
                  style={{ border: "1px solid rgba(214, 207, 196, 0.5)" }}
                >
                  <div className="flex items-center justify-between mb-5">
                    <span
                      className={`text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full ${
                        categoryColors[post.category] ?? "bg-pmu-amber-pale text-pmu-amber"
                      }`}
                      style={{ letterSpacing: "0.1em" }}
                    >
                      {post.category}
                    </span>
                    <span className="text-xs text-pmu-taupe">{post.readTime}</span>
                  </div>

                  <h3 className="font-fraunces font-semibold text-pmu-espresso text-xl leading-snug mb-3">
                    {post.title}
                  </h3>
                  <p className="text-pmu-taupe leading-relaxed text-sm flex-1 mb-6">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-pmu-taupe-light/20">
                    <span className="text-xs text-pmu-taupe">{post.date}</span>
                    <a
                      href={SUBSTACK_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-semibold text-pmu-amber hover:underline"
                    >
                      Read on Substack →
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom Substack CTA */}
        <section className="section-pad bg-pmu-cream">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="reveal-hidden">
              <h2
                className="font-fraunces font-bold text-pmu-espresso mb-6"
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)" }}
              >
                Never miss a post
              </h2>
              <p className="text-pmu-taupe mb-8 leading-relaxed">
                Subscribe to our Substack for weekly stories on nutrition, wellness, and community — delivered straight to your inbox.
              </p>
              <a
                href={SUBSTACK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Subscribe on Substack
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
